import { useState, useEffect, useCallback } from 'react';
import { publicApi, formatApiError } from '@/lib/api';
import { fmtEdTime } from '@/lib/tz';
import { Delete, LogIn, LogOut, Coffee, Play, Clock } from 'lucide-react';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'];
const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

const STATUS_LABEL = { OUT: 'Not clocked in', IN: 'Currently clocked in', ON_BREAK: 'On a break' };

export default function TimeClock() {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [identity, setIdentity] = useState(null); // { staff, status, since, todayMinutes }
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 15);
    return () => clearInterval(t);
  }, []);

  const reset = useCallback(() => { setIdentity(null); setResult(null); setPin(''); setError(''); }, []);

  // Auto-dismiss the confirmation card and return to the keypad.
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(reset, 6000);
    return () => clearTimeout(t);
  }, [result, reset]);

  const identify = useCallback(async (value) => {
    setBusy(true);
    try {
      const { data } = await publicApi.post('/timeclock/identify', { pin: value });
      setIdentity({ pin: value, ...data });
      setPin('');
      setError('');
    } catch (err) {
      setError(formatApiError(err));
      setPin('');
    } finally {
      setBusy(false);
    }
  }, []);

  const press = (key) => {
    if (busy) return;
    if (key === 'back') return setPin((p) => p.slice(0, -1));
    if (key === '' || pin.length >= 4) return;
    const next = pin + key;
    setPin(next);
    setError('');
    if (next.length === 4) identify(next);
  };

  const doAction = async (action) => {
    setBusy(true);
    try {
      const { data } = await publicApi.post('/timeclock/action', { pin: identity.pin, action });
      setResult(data);
      setIdentity(null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const ACTION_META = {
    CLOCK_IN: { label: 'Welcome', verb: 'Clocked in', icon: LogIn, tone: 'coriander' },
    CLOCK_OUT: { label: 'Goodbye', verb: 'Clocked out', icon: LogOut, tone: 'saffron' },
    START_BREAK: { label: 'Enjoy your break', verb: 'Break started', icon: Coffee, tone: 'amber' },
    END_BREAK: { label: 'Welcome back', verb: 'Break ended', icon: Play, tone: 'coriander' },
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6 py-10" data-testid="timeclock-page">
      <div className="w-full max-w-xs text-center">
        <img src="/logo-emblem.png" alt="The Tiffin by The Virsa" className="mx-auto w-20 h-20 rounded-full object-cover shadow-2xl ring-1 ring-white/10" />
        <p className="text-white/90 font-heading text-lg font-700 mt-4">Time Clock</p>
        <p className="text-white/40 text-xs mt-1 flex items-center justify-center gap-1.5">
          <Clock size={13} /> {fmtEdTime(now)} · {now.toLocaleDateString('en-CA', { timeZone: 'America/Edmonton', weekday: 'short', month: 'short', day: 'numeric' })}
        </p>

        {result ? (
          <ResultCard result={result} meta={ACTION_META[result.action]} onDone={reset} />
        ) : identity ? (
          <ActionCard identity={identity} busy={busy} error={error} onAction={doAction} onCancel={reset} />
        ) : (
          <>
            <p className="text-white/50 text-sm mt-6 mb-6">Enter your PIN to punch in or out</p>
            <div className="flex items-center justify-center gap-3 mb-6" data-testid="timeclock-pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`h-4 w-4 rounded-full border-2 border-saffron transition-colors duration-150 ${i < pin.length ? 'bg-saffron' : 'bg-transparent'}`} />
              ))}
            </div>
            {error && <p className="text-sm text-red-400 mb-4" data-testid="timeclock-error">{error}</p>}
            {busy && <p className="text-sm text-white/60 mb-4">Checking…</p>}
            <div className="grid grid-cols-3 gap-3">
              {KEYS.map((key, i) =>
                key === '' ? <div key={i} /> : (
                  <button
                    key={i}
                    data-testid={key === 'back' ? 'timeclock-pin-backspace' : `timeclock-pin-key-${key}`}
                    onClick={() => press(key)}
                    className="h-16 rounded-2xl bg-white/10 hover:bg-white/20 text-white text-2xl font-heading font-semibold flex items-center justify-center transition-colors duration-150"
                  >
                    {key === 'back' ? <Delete size={22} /> : key}
                  </button>
                ),
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ActionCard({ identity, busy, error, onAction, onCancel }) {
  const { staff, status, since, todayMinutes } = identity;
  const Btn = ({ testid, onClick, icon: Icon, label, cls }) => (
    <button data-testid={testid} disabled={busy} onClick={onClick} className={`w-full h-14 rounded-2xl font-heading font-semibold text-white flex items-center justify-center gap-2 transition-colors duration-150 disabled:opacity-50 ${cls}`}>
      <Icon size={20} /> {label}
    </button>
  );
  return (
    <div className="mt-6 rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 space-y-3" data-testid="timeclock-actions">
      <p className="text-white font-heading text-xl font-700">Hi, {staff.name}</p>
      <p className="text-white/50 text-xs">{STATUS_LABEL[status]}{since && status !== 'OUT' ? ` · since ${fmtEdTime(since)}` : ''}</p>
      <p className="text-white/40 text-xs">Today: {fmtDur(todayMinutes)}</p>
      {error && <p className="text-sm text-red-400" data-testid="timeclock-error">{error}</p>}
      <div className="space-y-2 pt-1">
        {status === 'OUT' && <Btn testid="action-clock-in" onClick={() => onAction('CLOCK_IN')} icon={LogIn} label="Clock In" cls="bg-coriander hover:bg-coriander/90" />}
        {status === 'IN' && <>
          <Btn testid="action-start-break" onClick={() => onAction('START_BREAK')} icon={Coffee} label="Start Break" cls="bg-amber-500 hover:bg-amber-500/90" />
          <Btn testid="action-clock-out" onClick={() => onAction('CLOCK_OUT')} icon={LogOut} label="Clock Out" cls="bg-saffron hover:bg-saffron-hover" />
        </>}
        {status === 'ON_BREAK' && <>
          <Btn testid="action-end-break" onClick={() => onAction('END_BREAK')} icon={Play} label="End Break" cls="bg-coriander hover:bg-coriander/90" />
          <Btn testid="action-clock-out" onClick={() => onAction('CLOCK_OUT')} icon={LogOut} label="Clock Out" cls="bg-saffron hover:bg-saffron-hover" />
        </>}
      </div>
      <button data-testid="action-cancel" onClick={onCancel} className="text-xs text-white/50 underline pt-1">Cancel</button>
    </div>
  );
}

function ResultCard({ result, meta, onDone }) {
  const Icon = meta.icon;
  const toneRing = { coriander: 'bg-coriander/15 ring-coriander/40', saffron: 'bg-saffron/15 ring-saffron/40', amber: 'bg-amber-500/15 ring-amber-500/40' }[meta.tone];
  const toneBtn = { coriander: 'bg-coriander', saffron: 'bg-saffron', amber: 'bg-amber-500' }[meta.tone];
  return (
    <div data-testid="timeclock-result" className={`mt-8 rounded-3xl p-6 ring-1 ${toneRing}`}>
      <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center text-white ${toneBtn}`}><Icon size={26} /></div>
      <p className="text-white font-heading text-xl font-700 mt-4">{meta.label}, {result.staff.name}!</p>
      <p className="text-white/70 text-sm mt-1">{meta.verb} at <span className="font-semibold text-white">{fmtEdTime(result.at)}</span></p>
      <p className="text-white/50 text-xs mt-2">Today: {fmtDur(result.todayMinutes)}</p>
      <button data-testid="timeclock-done-btn" onClick={onDone} className="mt-4 text-xs text-white/60 underline">Done</button>
    </div>
  );
}
